import { AdapterContext } from './adapter-context';
import { Metamodel } from '../base/model';
import { type Named } from '../base/named';
import { type IInitializableAdapter, staticConsoleLogger, BaseAdapter } from './base.adapter';
import { type ILogger } from '../../interfaces/ILogger';
import { type ITridentResource } from '../../interfaces/ITridentResource';
import { type IModelAdapterAsync } from './model-adapter-async';
import { LoadingStatusEnum } from '../../interfaces/IResourceFilesProvider';

/*
    (Grand)Mother of all adapters that require a context. Or the specific
    adapter has to have a initContext function
*/
export abstract class BaseAsyncAdapter<T, U, D = unknown> implements IInitializableAdapter, IModelAdapterAsync<T, U, D> {

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static convertObjectToString(message: any, keepStack?: boolean): string{
        if (typeof message !== 'string' && message != null) {
            let messageStr: string | undefined;
            if (typeof message.text === 'function') {
                try{
                    messageStr = message.text();
                }catch (e) {
                    // noop
                }
            }
            if (typeof messageStr === 'string'){
                return messageStr;
            }

            if (!keepStack && message instanceof Error) {
                // If it's Error, and don't want to keep the stack, use its message
                // By default, keepStack is true, it's used to log telemetry data
                // For components which shows error message to customer, keepStack should be false
                messageStr = message.message;
            } else {
                try {
                    messageStr = (message.error) ? JSON.stringify(message.error, Object.getOwnPropertyNames(message.error)) : JSON.stringify(message, Object.getOwnPropertyNames(message));
                } catch {
                    try {
                        messageStr = JSON.stringify(message);
                    } catch {
                        messageStr = '' + message;
                    }
                }
            }
            return messageStr;
        }
        return message;
    }

    private static readonly className = 'BaseAsyncAdapter';
    protected context: AdapterContext;

    public initContext(context: AdapterContext): void {
        this.context = context;
    }

    public async toResource(named: T, ...other: unknown[]): Promise<U> {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (named && !(named as any).isOk) {
                // We do not want to save/publish bad resources.
                // Ideally, the control should not come over here because
                // bad resources are not editable.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const resourceName = (named as any).name ? (named as any).name : '';
                throw new Error(`Unable to save ${resourceName}. Bad resource`);
            }
            const resource = await this._toResource(named, ...other);
            if (this.context?.adapterConfig?.postProcessResource) {
                this.context.adapterConfig.postProcessResource(resource);
            }
            return resource;
        } catch (error) {
            const model = Metamodel.modelOf(named).name;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const name = (named as any).name;
            const errorStr = `toResource failed. Reason: ${BaseAsyncAdapter.convertObjectToString(error)}`;
            const logString = JSON.stringify({
                model,
                name,
                error: errorStr
            });
            this._logError(logString);
            throw new Error(error);
        }
    }

    public async toModel(resource: U, ...other: unknown[]): Promise<T> {
        try {
            if (this.context?.adapterConfig?.preProcessResource) {
                resource = { ...resource };
                this.context.adapterConfig.preProcessResource(resource);
            }
            return await this._toModel(resource, ...other);
        } catch (error) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const aresource = (resource as any);
            const resourceType = aresource.type;
            const name = aresource.name;
            const errorStr = `toModel failed. Reason: ${BaseAsyncAdapter.convertObjectToString(error)}`;
            const logString = JSON.stringify({
                resourceType,
                name,
                error: errorStr
            });
            this._logError(logString);

            // For pipeline, dataset, trigger, linkedservice and IR, the first item in 'other'
            // will be the named, which the respective adapter will fill-in and return.
            // A better approach would be to let the individual adapters return the bad resources.
            // But this would require changes in all the adapters, so settling with this for now.
            // If _toModel fails, then return a bad model.
            // Only linkedservice._toModel does not go through base-async.adapter, stil having check for safety.
            if (other && other.length) {
                const named = other[0] as Named;
                if (named) {
                    const model = Metamodel.modelOf(named);
                    if (model && model.concept && model.concept.name) {
                        // eslint-disable-next-line max-len
                        if (['pipeline', 'dataset', 'dataflow', 'pbidataflow', 'datamapper', 'adfcdc', 'linkedservice', 'trigger', 'integrationruntime', 'pipelinesettings'].includes(model.concept.name.toLowerCase())) {
                            named.status = 'Bad';
                            named.loadError.status = LoadingStatusEnum.UnknownError;
                            named.loadError.error = error;
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            return named as any;
                        }
                    }

                }
            }
            throw new Error(error);
        }
    }

    public async toAzureResource(tridentResource: ITridentResource): Promise<U> {
        try {
            return await this._toAzureResource(tridentResource);
        } catch (e) {
            const errorStr = `toAzureResource failed. Reason: ${BaseAsyncAdapter.convertObjectToString(e)}`;
            const logString = JSON.stringify({
                artifactType: tridentResource.artifactType,
                name: tridentResource.displayName,
                objectId: tridentResource.objectId,
                datasourceType: tridentResource.datasourceType,
                error: errorStr
            });
            this._logError(logString);
            throw e;
        }
    }

    public async postToModel(value: T): Promise<void> {
        return;
    }

    public async toFabricCopilotContext(model: T): Promise<D> {
        return undefined;
    }
    
    public async fromFabricCopilotContext(context: D, model?: T): Promise<T> {
        return model;
    }

    protected abstract _toResource(named: T, ...other: unknown[]): Promise<U>;

    protected abstract _toModel(resource: U, ...other: unknown[]): Promise<T>;

    protected abstract _toAzureResource(tridentResource: ITridentResource): Promise<U>;

    protected _logError(error: string, properties?: Record<string, string>): void {
        this.ensureLogger().error(BaseAsyncAdapter.className, error, properties);
    }

    protected _logWarning(warning: string, properties?: Record<string, string>): void {
        this.ensureLogger().warn(BaseAsyncAdapter.className, warning, properties);
    }

    protected _logEvent(event: unknown): void {
        this.ensureLogger().logEvent(event);
    }

    protected _logInfo(message: string, properties?: Record<string, string>): void {
        this.ensureLogger().log(BaseAsyncAdapter.className, message, properties);
    }

    protected _cachedLogger: ILogger | undefined;

    protected ensureLogger(): ILogger {
        if (this._cachedLogger) {
            return this._cachedLogger;
        }

        if (this.context?.contextData) {
            this._cachedLogger = BaseAdapter.getCachedLoggerSync(this.context.contextData);
        }

        return this._cachedLogger ?? staticConsoleLogger;
    }
}
